import React from 'react';
import env from 'env';
import siteConfig from '../../config';
import { useGet, Loading, ErrorPopup } from 'components';

function isoDate(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function ExportCsv() {
  const get = useGet();

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [productId, setProductId] = React.useState('');
  const [startDate, setStartDate] = React.useState(isoDate(firstOfMonth));
  const [endDate, setEndDate] = React.useState(isoDate(today));
  const [downloading, setDownloading] = React.useState(false);
  const [downloadError, setDownloadError] = React.useState(null);

  React.useEffect(() => {
    get.send(env.apiBase + '/api/analyticsAdmin/getExportableProducts');
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    if (get.done() && get.response && get.response.length > 0 && !productId) {
      setProductId(get.response[0].productId);
    }
    // eslint-disable-next-line
  }, [get.status]);

  if (get.loading()) {
    return <Loading />;
  } else if (get.hasErrors()) {
    return <ErrorPopup title="Loading Error" errors={get.errors} />;
  }

  const products = get.response || [];

  async function handleExport() {
    setDownloadError(null);

    if (!productId) {
      setDownloadError('Please select a product.');
      return;
    }
    if (!startDate || !endDate) {
      setDownloadError('Please select both a start and end date.');
      return;
    }
    if (startDate > endDate) {
      setDownloadError('Start date must be on or before end date.');
      return;
    }

    setDownloading(true);
    try {
      const options = {};
      if (siteConfig.apiNetJwt) {
        await siteConfig.apiNetJwt(options);
      }

      const url = env.apiBase
        + '/api/analyticsAdmin/exportCsv'
        + `?productId=${encodeURIComponent(productId)}`
        + `&startDate=${encodeURIComponent(startDate)}`
        + `&endDate=${encodeURIComponent(endDate)}`;

      const res = await fetch(url, { method: 'GET', ...options });
      if (!res.ok) {
        const statusText = res.status === 401 ? 'Permission denied. Please login first.' : res.statusText;
        throw new Error(`Error ${res.status} ${statusText || ''}`.trim());
      }

      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      const fallback = `export-${startDate}-${endDate}.csv`;
      const fileName = match ? decodeURIComponent(match[1]) : fallback;

      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
    } catch (err) {
      setDownloadError(err.message || String(err));
    } finally {
      setDownloading(false);
    }
  }

  return (
    <>
      <h1>Export CSV</h1>
      <h5 style={{ marginBottom: '1.5rem', color: '#6c757d' }}>
        Export paid-order buyers for a product as a CSV (Email, Name).
      </h5>

      <div className="row">
        <div className="col-md-6">
          <div className="form-group">
            <label htmlFor="exportProduct"><strong>Product</strong></label>
            <select
              id="exportProduct"
              className="form-control"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              disabled={products.length === 0}
            >
              {products.length === 0 && <option value="">No products available</option>}
              {products.map((p) => (
                <option key={p.productId} value={p.productId}>{p.productName}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-3">
          <div className="form-group">
            <label htmlFor="exportStart"><strong>From</strong></label>
            <input
              id="exportStart"
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-3">
          <div className="form-group">
            <label htmlFor="exportEnd"><strong>To</strong></label>
            <input
              id="exportEnd"
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div style={{ marginTop: '1rem' }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleExport}
          disabled={downloading || !productId}
        >
          {downloading ? 'Exporting...' : 'Export CSV'}
        </button>
        {downloadError && (
          <div style={{ marginTop: '0.75rem', color: '#c0392b' }}>{downloadError}</div>
        )}
      </div>
    </>
  );
}
